import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCheck, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button, Card, EmptyState, ErrorState, PageLoader, cx } from "@/components/ui";
import { useToast } from "@/components/Toast";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { useNotifications } from "@/lib/queries";
import type { NotificationDTO } from "@shared/types";

const KIND_ICONS: Record<string, string> = {
  family: "👪",
  usage: "⏱️",
  redemption: "🛍️",
  bonus: "🎉",
  responsibility: "✅",
  unlocked: "🔓",
  goal: "🎯",
  deduction: "⚠️",
  recovery: "💪",
  job: "🧹",
  pitch: "💡",
  job_submitted: "📬",
  job_approved: "🪙",
  job_rejected: "↩️",
};

/** Shared by parents (/parent/notifications) and kids (/kid/notifications). */
export function Notifications() {
  const { data, isLoading, error, refetch } = useNotifications();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  // Remember what was unread when the page opened so it stays highlighted after we mark it read.
  const [fresh, setFresh] = useState<Set<string> | null>(null);
  const [marking, setMarking] = useState(false);

  async function markAllRead(silent = false) {
    setMarking(true);
    try {
      await api.post("/notifications/read-all");
      await qc.invalidateQueries({ queryKey: ["notifications"] });
    } catch (err) {
      if (!silent) toast.show((err as Error).message, "error");
    } finally {
      setMarking(false);
    }
  }

  useEffect(() => {
    if (!data || fresh) return;
    const unreadIds = new Set(data.notifications.filter((n) => !n.readAt).map((n) => n.id));
    setFresh(unreadIds);
    if (unreadIds.size) void markAllRead(true);
  }, [data, fresh]);

  if (isLoading) return <PageLoader />;
  if (error || !data) {
    return (
      <div>
        <PageHeader title="Notifications" back />
        <ErrorState error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  const list = data.notifications;
  const isNew = (n: NotificationDTO) => !n.readAt || !!fresh?.has(n.id);

  function open(n: NotificationDTO) {
    if (!n.link) return;
    if (/^https?:\/\//i.test(n.link)) window.open(n.link, "_blank", "noopener,noreferrer");
    else navigate(n.link);
  }

  return (
    <div>
      <PageHeader
        title="Notifications"
        back
        actions={
          data.unread > 0 ? (
            <Button size="sm" variant="secondary" onClick={() => markAllRead()} loading={marking}>
              <CheckCheck className="size-4" /> Mark all read
            </Button>
          ) : undefined
        }
      />

      {list.length === 0 ? (
        <div className="mt-4">
          <EmptyState icon="🎉" title="You're all caught up">
            Approvals, rewards and family news will show up here.
          </EmptyState>
        </div>
      ) : (
        <Card className="mt-4 p-1.5">
          <ul className="divide-y divide-line/70">
            {list.map((n) => {
              const unread = isNew(n);
              const body = (
                <>
                  <span
                    className={cx(
                      "flex size-11 shrink-0 items-center justify-center rounded-2xl text-xl",
                      unread ? "bg-brand-soft" : "bg-sunken",
                    )}
                    aria-hidden
                  >
                    {KIND_ICONS[n.kind] ?? "🔔"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start gap-2">
                      <span className={cx("min-w-0 flex-1 leading-snug", unread ? "font-extrabold text-ink" : "font-semibold text-ink/80")}>
                        {n.title}
                      </span>
                      <span className="shrink-0 pt-0.5 text-xs font-semibold text-faint">{timeAgo(n.createdAt)}</span>
                    </span>
                    {n.body && <span className="mt-0.5 block text-sm text-muted">{n.body}</span>}
                  </span>
                  {unread && <span className="mt-2 size-2.5 shrink-0 rounded-full bg-brand" aria-label="New" />}
                  {n.link && !unread && <ChevronRight className="mt-2.5 size-5 shrink-0 text-faint" />}
                </>
              );
              return (
                <li key={n.id}>
                  {n.link ? (
                    <button
                      type="button"
                      onClick={() => open(n)}
                      className="flex w-full items-start gap-3 rounded-2xl px-3 py-3 text-left transition hover:bg-sunken"
                    >
                      {body}
                    </button>
                  ) : (
                    <div className="flex items-start gap-3 px-3 py-3">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
